/*
 * Developed by CQ Inversiones SAS.  Copyright (c) 2024. All rights reserved.
 * Desarrollado por CQ Inversiones SAS.  Copyright (c) 2024. Todos los derechos reservados.
 */

import {inject, Injectable} from '@angular/core';
import {
  BehaviorSubject,
  catchError, delay,
  EMPTY,
  map,
  Observable, pipe,
  Subject,
  switchMap,
  tap,
  UnaryFunction,
} from 'rxjs';
import {IconFormInterface, IconListInterface} from '@shared/interface';
import {MkadIconsService} from '@shared/services';
import {HttpErrorResponse} from '@angular/common/http';
import {NO_INDEX_FIND} from '@shared/constants';

export function handleResponse<TResponse, TError = Error>(
  onSuccess: (response: TResponse) => void,
  onError?: (error: TError) => void,
  onFinally?: () => void,
): UnaryFunction<Observable<TResponse>, Observable<TResponse>> {
  return pipe(
    tap({next: onSuccess, complete: onFinally}),
    catchError((error: TError) => {
      onError?.(error);
      onFinally?.();
      return EMPTY;
    }),
  );
}

export interface CallbacksInterface<T = unknown> {
  callback: (response?: T) => void;
  error?: (error: HttpErrorResponse) => void;
}

export interface IconStoreInterface {
  iconList: IconListInterface[];
  iconsSelected: IconListInterface[];
  iconsSelectedCount: number;
  iconsFiltered: IconListInterface[];
  filtering: boolean;
  loading: boolean;
}

export const IconStoreInitialState: IconStoreInterface = {
  iconList: [],
  iconsSelected: [],
  iconsSelectedCount: 0,
  iconsFiltered: [],
  filtering: false,
  loading: false,
};
export const FIVE_HUNDRED = 500;
const TIMER_DELAY = 300;
export const VIEW_LOAD_DELAY = FIVE_HUNDRED * 4;

@Injectable({
  providedIn: 'root',
})
export class MkadIconsStore {
  private state$ = new BehaviorSubject<IconStoreInterface>(IconStoreInitialState);
  private queryIconsList$ = new Subject<CallbacksInterface<IconListInterface[]> | null>();
  private filterIconsList$ = new Subject<string>();
  private deleteIcons$ = new Subject<CallbacksInterface>();
  private createdIcon$ = new Subject<IconFormInterface>();
  private updatedIconU$ = new Subject<IconFormInterface>();
  private readonly iconsService: MkadIconsService = inject(MkadIconsService);
  // observables selects
  readonly iconsFiltered$: Observable<IconListInterface[]> = this.state$.pipe(map((state) => (state.iconsFiltered)));
  readonly iconsList$: Observable<IconListInterface[]> = this.state$.pipe(map((state) => (state.iconList)));
  readonly iconsSelectedCount$: Observable<number> = this.state$.pipe(map((state) => (state.iconsSelectedCount)));
  readonly iconsSelected$: Observable<IconListInterface[]> = this.state$.pipe(map((state) => (state.iconsSelected)));
  readonly isFiltering$: Observable<boolean> = this.state$.pipe(map((state) => (state.filtering)));
  readonly isLoading$: Observable<boolean> = this.state$.pipe(map((state) => (state.loading)));
  private readonly itemsPerPage = FIVE_HUNDRED;
  private currentPage = 0;
  private unsubscribe$ = new Subject<void>();

  constructor() {
    this.initEffects();
  }

  startLoading(prop: keyof IconStoreInterface, state: boolean): void {
    const current = this.state$.value;
    this.state$.next({...current, [prop]: state});
  }

  private handleErrorResponse(prop: keyof IconStoreInterface): Observable<never> {
    this.startLoading(prop, false);
    return EMPTY;
  }

  getFullIcons(): IconListInterface[] {
    return this.state$.value.iconList;
  }

  resetFilteredList(): void {
    const current = this.state$.value;
    const cleaned = current.iconList.map((icon) => ({...icon, selected: false}));
    this.state$.next({...current, iconList: cleaned, iconsFiltered: cleaned, iconsSelected: []});
  }

  createIcon(icon: IconFormInterface): Observable<IconFormInterface> {
    return this.iconsService.create(icon).pipe(
      map((response) => {
        if (response) this.createdIcon$.next(response);
        return response;
      }),
    );
  }

  updateIcon(icon: IconFormInterface): Observable<IconFormInterface> {
    return this.iconsService.update(icon).pipe(
      map((response) => {
        if (response) this.updatedIconU$.next(icon);
        return response;
      }),
    );
  }

  deleteIcons(param: CallbacksInterface): void {
    this.startLoading('loading', true);
    this.deleteIcons$.next(param);
  }

  queryIcons(param: CallbacksInterface<IconListInterface[]> | null): void {
    this.startLoading('loading', true);
    this.queryIconsList$.next(param);
  }

  safeQueryIcons(param: CallbacksInterface<IconListInterface[]>): void {
    this.startLoading('loading', true);
    const state = this.state$.value;
    if (state.iconList.length === 0) {
      this.queryIconsList$.next(param);
    } else {
      param.callback(state.iconList);
      setTimeout(() => this.startLoading('loading', false), FIVE_HUNDRED);
    }
  }

  filterIconBy(query: number | string, param: 'id' | 'icon_class' = 'id'): IconListInterface | undefined {
    const current = this.state$.value;
    return current.iconList.find((icon) => icon[param] === query);
  }

  filterIcons(filter: string): void {
    this.filterIconsList$.next(filter);
  }

  handleClickCard(selected: IconListInterface, multi: boolean = true): void {
    this.startLoading('filtering', true);
    const current = this.state$.value;
    if (selected.selected) {
      this.handleIconSelected(multi, current, selected, false, current.iconsSelectedCount - 1);
    } else {
      this.handleIconSelected(multi, current, selected, true, current.iconsSelectedCount + 1);
    }
    this.state$.next({...current, filtering: false});
  }

  private handleIconSelected(multi: boolean, current: IconStoreInterface, selectedIcon: IconListInterface, selected: boolean, count: number) {
    if (multi) {
      this.setterSelectedIcon(current, selectedIcon, selected, count);
    } else {
      current.iconsSelectedCount = 1;
      current.iconsSelected = [selectedIcon];
      current.iconsFiltered.map((icon) => ({...icon, selected: false}));
      const filteredIndex = current.iconsFiltered.findIndex((icon) => icon.selected);
      if (filteredIndex > NO_INDEX_FIND) {
        current.iconsFiltered[filteredIndex].selected = false;
      }
      const selectedIndex = current.iconList.findIndex((icon) => icon.selected);
      if (selectedIndex > NO_INDEX_FIND) {
        current.iconList[selectedIndex].selected = false;
      }
      const validation = (icon: IconListInterface) => icon.id === selectedIcon.id;
      const filterIndex = current.iconsFiltered.findIndex(validation);
      if (filterIndex > NO_INDEX_FIND) {
        current.iconsFiltered[filterIndex].selected = true;
      }
      const iconIndex = current.iconList.findIndex(validation);
      if (iconIndex > NO_INDEX_FIND) {
        current.iconList[iconIndex].selected = true;
      }
    }
  }

  private setterSelectedIcon(current: IconStoreInterface, selectedIcon: IconListInterface, selected: boolean, count: number) {
    const validation = (icon: IconListInterface) => icon.id === selectedIcon.id;
    const filterIndex = current.iconsFiltered.findIndex(validation);
    current.iconsFiltered[filterIndex].selected = selected;
    const iconIndex = current.iconList.findIndex(validation);
    current.iconList[iconIndex].selected = selected;
    current.iconsSelectedCount = count;
  }

  private addMoreIcons(): void {
    const nextPage = this.currentPage + 1;
    const startIndex = this.currentPage * this.itemsPerPage;
    const endIndex = nextPage * this.itemsPerPage;
    const state = this.state$.value;
    const itemsToShow = [
      ...state.iconsFiltered,
      ...state.iconList.slice(startIndex, endIndex),
    ];
    this.state$.next({...state, iconsFiltered: itemsToShow, filtering: false, loading: false});
    this.currentPage = nextPage;
    this.startLoading('loading', false);
  }

  private loadMoreIcons(): void {
    const state = this.state$.value;
    const times = Math.ceil(state.iconList.length / this.itemsPerPage);
    if (this.currentPage < times) {
      this.addMoreIcons();
    } else {
      this.startLoading('loading', false);
      this.startLoading('filtering', false);
    }
  }

  finishDynamicLoad(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initEffects(): void {
    this.queryIconsList$
      .pipe(
        tap(() => {
          this.currentPage = 0;
          this.state$.next({...IconStoreInitialState, loading: true, filtering: true});
        }),
        switchMap((param) => {
          return this.iconsService.getList().pipe(
            handleResponse((response) => {
                const state = this.state$.value;
                if (!response) {
                  this.state$.next({...state, iconList: [], iconsFiltered: [], loading: false, filtering: false});
                  if (param) param.callback([]);
                  return;
                }
                const icons = response.map((icon) => ({...icon, selected: false}));
                this.state$.next({...state, iconList: icons, iconsFiltered: []});
                this.loadMoreIcons();
                if (param) param.callback(icons);
              },
              () => this.handleErrorResponse('loading'),
            ),
          );
        }),
      )
      .subscribe();
    this.deleteIcons$
      .pipe(
        switchMap((params) => {
          const state = this.state$.value;
          const toDelete = state.iconList.filter((icon) => icon.selected).map((icon) => icon.id);
          return this.iconsService.delete(toDelete).pipe(
            handleResponse(
              (response) => {
                if (response) params.callback();
                this.queryIcons(null);
              },
              (error: HttpErrorResponse) => {
                if (params.error) params.error(error);
                this.handleErrorResponse('loading');
              },
            ),
          );
        }),
      )
      .subscribe();
    this.createdIcon$
      .pipe(
        tap((created) => {
          if (created) {
            const state = this.state$.value;
            state.iconList.push({...created, id: (<number>created.id), selected: false});
            state.iconsFiltered.push({...created, id: (<number>created.id), selected: false});
            this.state$.next({...state, loading: false});
          }
        }),
      )
      .subscribe();
    this.updatedIconU$
      .pipe(
        tap((updated) => {
          if (updated) {
            const state = this.state$.value;
            const updatedIndex = state.iconList.findIndex((icon) => icon.id === updated.id);
            state.iconList[updatedIndex] = {
              ...updated,
              id: (<number>updated.id),
              selected: state.iconList[updatedIndex].selected,
            };
            const updatedFilterIndex = state.iconList.findIndex((icon) => icon.id === updated.id);
            state.iconList[updatedFilterIndex] = {
              ...updated,
              id: (<number>updated.id),
              selected: state.iconList[updatedFilterIndex].selected,
            };
            this.state$.next({...state, loading: false});
          }
        }),
      )
      .subscribe();
    this.filterIconsList$
      .pipe(
        delay(TIMER_DELAY),
        tap((filter) => {
          const state = this.state$.value;
          if (filter.trim().length === 0) {
            this.state$.next({...state, iconsFiltered: state.iconList, loading: false, filtering: false});
          } else {
            const filterClean = filter.toLowerCase().trim();
            const results: IconListInterface[] = state.iconList.filter((icon) => icon.icon_class.includes(filterClean) || String(icon.id).includes(filterClean));
            this.state$.next({...state, iconsFiltered: results, loading: false, filtering: false});
          }
        }),
      )
      .subscribe();
  }

}
